import React, { useMemo, useState } from 'react';
import Select, { components as SelectComponents } from 'react-select';
import { FixedSizeList } from 'react-window';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Button, Tooltip, Link } from '@mui/material';
import NoSsr from '@mui/material/NoSsr';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CancelIcon from '@mui/icons-material/Cancel';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import { formatter } from '#component/shipment/helper';
import OccupiedDeviceDialog from '#component/general/occupied-device-dialog';
import { buildInlineSelectStyles } from '#component/form/react-select-styles';
import {
  InputContainer,
  MenuPaper,
  MessageTypography,
  PlaceholderTypography,
  RootContainer,
  SingleValueTypography,
  StyledChip,
  StyledFormHelperText,
  ValueContainerDiv
} from './style';
import propTypes from './prop-types';

const KeyNameEnum = {
  ARROW_DOWN: 40,
  ARROW_UP: 38
};

const renderOccupier = s => (
  <span
    style={{
      color: '#667',
      marginLeft: '8px',
      fontSize: '12px',
      position: 'absolute',
      right: '32px',
      width: '140px'
    }}
  >
    {s.title || (s.uid && `#: ${formatter(s.uid)}`) || s.label}
  </span>
);

const RenderUsedBy = props => {
  const { usedBy: u, setOpenDialog } = props;

  return (
    <span
      style={{
        color: '#667',
        marginLeft: '8px',
        fontSize: '12px',
        position: 'absolute',
        right: '32px',
        width: '140px'
      }}
    >
      {u.length > 1 ?
        <Button
          size="small"
          onClick={e => {
            // prevent select
            e.stopPropagation();
            setOpenDialog(u[0].device);
          }}
        >
          {' '}
          {u.length}
          {' '}
          items more
          {' '}
        </Button> :
        (`${
          (u[0].title && `"${u[0].title}"`) ||
          (u[0].uid && `#: ${formatter(u[0].uid)}`) ||
          (u[0].label && `"${u[0].label}"`)}`
        )}
    </span>
  );
};

const NoOptionsMessage = props => (
  <MessageTypography color="textSecondary" {...props.innerProps}>
    {props.children}
  </MessageTypography>
);

const inputComponent = React.forwardRef(
  (props, ref) => <InputContainer ref={ref} {...props} />
);

const Control = props => (
  <TextField
    fullWidth
    InputProps={{
      inputComponent,
      inputProps: {
        children: props.children,
        ...props.innerProps
      }
    }}
    {...props.selectProps.textFieldProps}
  />
);

const Option = props => {
  const { disabledBy, usedBy } = props.data;
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      disabled={Boolean(disabledBy)}
      {...props.innerProps}
    >
      {props.children}
      {disabledBy && renderOccupier(disabledBy)}
      {usedBy && <RenderUsedBy usedBy={usedBy} setOpenDialog={props.selectProps.setOpenDialog} />}
    </MenuItem>
  );
};

const Placeholder = props => (
  <PlaceholderTypography color="textSecondary" {...props.innerProps}>
    {props.children}
  </PlaceholderTypography>
);

const SingleValue = props => (
  <SingleValueTypography {...props.innerProps}>
    {props.children}
  </SingleValueTypography>
);

const ValueContainer = props => (
  <ValueContainerDiv overviewPage={props.selectProps.overviewPage}>
    {props.children}
  </ValueContainerDiv>
);

const MultiValue = props => {
  const {
    data,
    data: { disabledBy, color, chipOverview },
    selectProps,
    children,
    isFocused,
    removeProps,
    setValue
  } = props;

  const warnProps = disabledBy ?
    {
      style: {
        color: 'red'
      },
      title: `Device in shipment #: ${formatter(disabledBy.uid)}`
    } : {};

  const notificationProps = {
    fontSize: 'small',
    onClick: () => {
      setValue(selectProps.value.map(v =>
        (v.label === children ? { ...v, alertOn: !data.alertOn } : v)));
    },
    onMouseDown: e => {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <StyledChip
      tabIndex={-1}
      label={(
        <>
          <Tooltip title="Open the device in a new tab">
            <Link
              href={`${window.location.origin}/device/${data.value}`}
              color="inherit"
              target="_blank"
              onMouseDown={e => e.stopPropagation()}
            >
              {children}
            </Link>
          </Tooltip>
          {
            data.showAlertIcon ?
              <Tooltip title={data.alertLabel || 'Does not have an alert'}>
                {data.alertOn ?
                  <NotificationsActiveRoundedIcon {...notificationProps} /> :
                  <NotificationsOffOutlinedIcon {...notificationProps} />}
              </Tooltip> :
              null
          }
        </>)}
      isFocused={isFocused}
      isChipOverview={chipOverview}
      style={{ color: selectProps.isChipsColored && `#${color}` }}
      onDelete={removeProps.onClick}
      deleteIcon={
        <Tooltip title="Remove">
          <CancelIcon {...removeProps} style={{ color: selectProps.isChipsColored && `#${color}` }} />
        </Tooltip>
      }
      {...warnProps}
      disabled={selectProps.isDisabled}
    />
  );
};

const Menu = props => {
  const { placement } = props;
  return (
    <MenuPaper
      square
      {...props.innerProps}
      style={placement === 'top' ? { bottom: '100%' } : { top: '100%' }}
    >
      {props.children}
    </MenuPaper>
  );
};

const FixedSizeListItem = ({ index, style, data }) => <div style={style}>{data[index]}</div>;

const MenuList = props => {
  const fixedSizeListRef = React.useRef(null);
  const currentIndex = React.useRef(0);
  const [keyCode, setKeyCode] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const itemSize = isMobile ? 40 : 36;

  React.useEffect(() => {
    const handleKeyDown = event => {
      if (Object.values(KeyNameEnum).includes(event.keyCode)) {
        setKeyCode(event.keyCode);
      }
    };

    let el;

    if (props.selectProps.id) {
      el = document.getElementById(props.selectProps.id);

      if (el) {
        el.addEventListener('keydown', handleKeyDown);
      }
    }

    return () => {
      if (el) {
        el.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [props.selectProps.id]);

  React.useLayoutEffect(() => {
    if (keyCode && Array.isArray(props.children)) {
      let newIndex = currentIndex.current;

      switch (keyCode) {
        case KeyNameEnum.ARROW_DOWN:
          newIndex += 1;
          break;
        case KeyNameEnum.ARROW_UP:
          newIndex -= 1;
          break;
        default:
      }

      fixedSizeListRef.current.scrollToItem(newIndex, 'smart');
      setKeyCode(null);
    }
  }, [keyCode, props.children]);

  return (
    <SelectComponents.MenuList {...props}>
      {Array.isArray(props.children) ? (
        <FixedSizeList
          ref={fixedSizeListRef}
          height={Math.min(200, itemSize * props.children.length)}
          width="100%"
          itemSize={itemSize}
          itemData={props.children}
          itemCount={props.children.length}
        >
          {({ index, data, ...rest }) => {
            if (data[index].props.isFocused) {
              currentIndex.current = index;
            }

            return <FixedSizeListItem index={index} data={data} {...rest} />;
          }}
        </FixedSizeList>
      ) : (
        props.children
      )}
    </SelectComponents.MenuList>
  );
};

const LoadingMessage = props => (
  <MessageTypography color="textSecondary" {...props.innerProps}>
    {props.selectProps.inputValue.length === 1 ? 'Enter 2 chars for searching' : 'Loading...'}
  </MessageTypography>
);

const defaultComponents = {
  Control,
  Menu,
  MenuList,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  LoadingMessage
};

const IntegrationReactSelect = props => {
  const {
    id,
    className = '',
    label,
    data,
    placeholder,
    defaultData,
    loading,
    setData,
    fetchDevices,
    clearDevices,
    textFieldOnFocus,
    meta = {},
    isChipsColored,
    components: componentsOverride = {},
    disabled,
    menuPlacement = 'bottom',
    forceError,
    member,
    overviewPage
  } = props;

  const theme = useTheme();
  const invalid = meta && !meta.valid && (meta.submitFailed || forceError);
  const components = useMemo(
    () => ({ ...defaultComponents, ...componentsOverride }),
    [componentsOverride]
  );

  const [dataDialog, setOpenDialog] = useState(false);

  const handleChange = (value, { action }) => {
    setData(value);
    if (clearDevices &&
      // ignore click on the bell icon
      action !== 'set-value') {
      clearDevices();
    }
  };

  const selectStyles = useMemo(() => buildInlineSelectStyles({ theme }), [theme]);

  const handleFilter = ({ data: dataOptions }, inputValue) => {
    const dataNoType = { ...dataOptions };
    delete dataNoType.type;
    delete dataNoType.typeForRender;
    const dataValues = Object.values(dataNoType)
      .reduce((a, c) => {
        if (typeof c === 'string') {
          a.push(c.replace(/\s/g, '').toLowerCase());
        }

        return a;
      }, []);
    const clearInputValue = inputValue.replace(/\s/g, '').toLowerCase();
    const res = dataValues.some(v => v.includes(clearInputValue));

    return res;
  };

  if (!id) {
    console.warn('[Multiple select] Please specify id in order to enable menu-item scroll');
  }

  return (
    <RootContainer className={`${invalid ? 'invalid' : ''} ${className}`}>
      <NoSsr>
        <Select
          id={id}
          styles={selectStyles}
          textFieldProps={{
            label,
            onChange: fetchDevices || null,
            onBlur: clearDevices || null,
            onFocus: textFieldOnFocus || null,
            InputLabelProps: {
              shrink: true
            }
          }}
          placeholder={placeholder}
          options={loading ? [] : data}
          components={components}
          value={defaultData}
          onChange={handleChange}
          isMulti
          isLoading={loading}
          isChipsColored={isChipsColored}
          filterOption={handleFilter}
          isDisabled={disabled}
          menuPlacement={menuPlacement}
          isOptionDisabled={option => !!option.disabledBy}
          captureMenuScroll={false}
          setOpenDialog={setOpenDialog}
          member={member}
          overviewPage={overviewPage}
        />
        {(meta.touched || forceError) && meta.invalid && (
          <StyledFormHelperText error>
            {meta.error}
          </StyledFormHelperText>
        )}
      </NoSsr>
      <OccupiedDeviceDialog setOpenDialog={setOpenDialog} dataDialog={dataDialog} />
    </RootContainer>
  );
};

IntegrationReactSelect.propTypes = propTypes;

export default IntegrationReactSelect;
